/**
 * Pure helpers for the Payment page's multi-select "pay and print" batch.
 *
 * The grid on the Payment page lists a client's payable contracts. An operator can
 * tick several of them and settle each in one go; a bill only exists once a payment
 * has been processed, so the batch is a sequence of real financial writes rather than
 * a reporting action. Everything in this module is side-effect free — the request
 * sequencing itself lives in the page, because it has to touch the cash drawer,
 * the Sumator and the PDF download.
 */

import { ContractPaymentSettings } from './paymentContracts';

/** Minimal shape this module needs from a contract row. */
export interface BatchContract {
    Nr: number;
    NrContract: string;
}

/** Minimal shape this module needs from a payment calculation. */
export interface BatchCalculation {
    totalIntegral: number;
    totalExtension: number;
    discount: number;
    canExtend: boolean;
}

/** Minimal shape this module needs from a contract's payment settings. */
export interface BatchSettings {
    paymentType: 'full' | 'extension';
    applyDiscount: boolean;
}

/** Settings a newly selected contract starts with — a discounted full redemption. */
export const defaultPaymentSettings = (): ContractPaymentSettings => ({
    paymentType: 'full',
    extensionDays: 30,
    applyDiscount: true,
    contractBonusPercent: null,
});

/**
 * Settings for one contract, falling back to the defaults for a contract that has just
 * been ticked and has no entry yet.
 */
export const getPaymentSettings = (
    settings: Map<number, ContractPaymentSettings>,
    contractNr: number,
): ContractPaymentSettings => settings.get(contractNr) ?? defaultPaymentSettings();

/**
 * What one contract will be charged under its own settings.
 *
 * Mirrors the details card: a redemption settles `totalIntegral` and a prolongation
 * `totalExtension`, and declining the discount adds it back on — the calculation
 * endpoint returns both totals already net of it.
 *
 * A contract that cannot be extended is priced as a full payment regardless of what
 * its settings say, so a stale `extension` choice can never quote a prolongation the
 * backend would refuse.
 */
export const getAmountForSettings = (
    calculation: BatchCalculation,
    settings: BatchSettings,
): number => {
    const extending = settings.paymentType === 'extension' && calculation.canExtend;
    const total = extending ? calculation.totalExtension : calculation.totalIntegral;

    return settings.applyDiscount ? total : total + calculation.discount;
};

/**
 * Resolve selected contract ids back to rows, in grid order.
 *
 * Selection is held as ids rather than row objects so it survives a refetch: the
 * contract list is reloaded after every batch, which replaces every row object. Ids
 * with no matching row simply do not appear, which is what retires a contract that has
 * just been paid off and has therefore left the payable list.
 *
 * Grid order — rather than the order rows were ticked — is deliberate: it is the order
 * the confirmation dialog lists, the order contracts are settled in, and therefore the
 * order the printed bills come out in.
 */
export const getSelectedContracts = <T extends BatchContract>(
    selection: number[],
    contracts: T[],
): T[] => contracts.filter(contract => selection.includes(contract.Nr));

/**
 * Which contracts get a details panel under the payable-contracts table.
 *
 * The checkbox selection drives it, so ticking several contracts shows several panels.
 * With nothing ticked it falls back to the row-clicked contract, which is the behaviour
 * the page has always had — row clicks deliberately do not tick anything, because
 * inspecting a contract must never enrol it in a batch that charges money.
 *
 * Both sources are resolved against `payableContracts` by id rather than being held as
 * row objects. That is what retires a panel once its contract has been paid off: a
 * redeemed contract leaves the payable list on the post-payment refetch, so it can no
 * longer be found here. Holding the row-clicked contract as an object instead used to
 * leave the bill of a contract that had just been paid on screen after the batch
 * selection had already cleared itself.
 */
export const getPanelContracts = <T extends BatchContract>(
    selection: number[],
    inspectedContractNr: number | null,
    payableContracts: T[],
): T[] => {
    const selected = getSelectedContracts(selection, payableContracts);

    if (selected.length > 0) {
        return selected;
    }

    const inspected = payableContracts.find(contract => contract.Nr === inspectedContractNr);

    return inspected ? [inspected] : [];
};

export interface BatchLine {
    contract: BatchContract;
    amount: number;
    settings: ContractPaymentSettings;
}

export interface BatchSummary {
    lines: BatchLine[];
    /** Contracts whose calculation could not be loaded; the batch cannot price these. */
    unpriced: BatchContract[];
    total: number;
}

/**
 * Price a batch, each contract under its own settings, so a selection may mix a
 * redemption with a prolongation and still produce one total.
 */
export const summarizeBatchPayment = (
    contracts: BatchContract[],
    calculations: Map<number, BatchCalculation>,
    settings: Map<number, ContractPaymentSettings>,
): BatchSummary => {
    const lines: BatchLine[] = [];
    const unpriced: BatchContract[] = [];

    for (const contract of contracts) {
        const calculation = calculations.get(contract.Nr);

        if (!calculation) {
            unpriced.push(contract);
            continue;
        }

        const contractSettings = getPaymentSettings(settings, contract.Nr);

        lines.push({
            contract,
            amount: getAmountForSettings(calculation, contractSettings),
            settings: contractSettings,
        });
    }

    return {
        lines,
        unpriced,
        total: lines.reduce((sum, line) => sum + line.amount, 0),
    };
};

export interface BatchPaidContract {
    contractNumber: string;
    invoiceNumber: string;
    amount: number;
    /** The payment went through but its bill could not be fetched. */
    printFailed: boolean;
}

export interface BatchOutcome {
    paid: BatchPaidContract[];
    /** Set when a payment failed; everything after it was left untouched. */
    failedContractNumber: string | null;
    failureReason: string | null;
    /** Contracts never attempted because the run stopped early. */
    notAttempted: string[];
}

/**
 * Classify a finished run for the result banner.
 *
 * A payment failure stops the batch, so anything after it is reported as untouched
 * rather than failed. A *print* failure does not stop the batch: the money has already
 * moved and the invoice number is recorded, so the bill stays reprintable and halting
 * would only leave the remaining contracts unpaid for no benefit.
 */
export const getBatchOutcomeSeverity = (outcome: BatchOutcome): 'success' | 'warning' | 'error' => {
    if (outcome.failedContractNumber !== null) {
        return outcome.paid.length > 0 ? 'warning' : 'error';
    }

    return outcome.paid.some(contract => contract.printFailed) ? 'warning' : 'success';
};
